import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Box, Grid, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { find, isEmpty } from 'lodash';
import React from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import CAN from '../../../images/can.svg';
import { findMyDevice } from '../../../utils/api';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import DeviceConfigurationsModal from './DeviceConfigurationsModal';
import Switch from '../../../components/Switch';
import Button from '../../../components/Button';
import SelectInput from '../../../components/SelectInput';

const DeviceCard = ({ device, user, cr }) => {
  const history = useNavigate();
  const [activeDevice, setActiveDevice] = React.useState(device[0] || null);

  const goal = React.useMemo(() => user?.goal?.daily.at(-1)?.value || 30, [user]);

  const [emergencySubscription, setEmergencySubscription] = React.useState(null);
  const [showDeviceConfigModal, setShowDeviceConfigModal] = React.useState(false);

  React.useEffect(() => {
    if (!isEmpty(user)) {
      const data = find(user?.stripeInfo?.subscriptions, (i) => i.type === 'EMERGENCY');
      setEmergencySubscription(data);
    }
  }, [user]);

  const handleCopyPhone = (device) => {
    if (!isEmpty(device?.deviceInfo?.phoneno)) {
      navigator.clipboard.writeText(device?.deviceInfo?.phoneno);
      toast(`${device?.deviceInfo?.phoneno} copied to clipboard`);
    }
  };

  const findDevice = useMutation(findMyDevice, {
    onSuccess: (data) => {
      toast(data?.results || 'Your CANgo should peep and vibrate now');
    },
    onError: (error) => {
      console.log('error =====> ', error);
    },
  });

  const handleFindDevice = (device) => {
    findDevice.mutate({ userId: user?._id, deviceId: device?._id });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '570px',
        gap: 2,
        p: { xs: 2, md: 3 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        margin: '0 auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            src={CAN}
          />
          <Box>
            <Box style={{ marginBottom: '10px' }}>
              <SelectInput
                options={device?.map((device) => device?.name)}
                value={activeDevice?.name}
                onChange={(e) => {
                  const { value } = e.target;
                  const deviceData = find(device, (i) => i.name === value);
                  setActiveDevice(deviceData);
                }}
              />
            </Box>

            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
              px={2}
              py={1}
              bgcolor="rgba(244, 250, 236, 1)"
              borderRadius="100px"
            >
              <Avatar
                sx={{
                  bgcolor: 'rgba(140, 208, 60, 1)',
                  width: 24,
                  height: 24,
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', width: 16, height: 16 }} />
              </Avatar>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Roboto, Helvetica',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '14px' },
                  color: 'rgba(29, 29, 31, 1)',
                }}
              >
                Connected
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={() => handleFindDevice(activeDevice)} text="Find Cane" />
          <IconButton
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '50px',
              width: 32,
              height: 32,
            }}
            onClick={() => setShowDeviceConfigModal(true)}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              borderRadius: 2,
              bgcolor: '#f5f5f7',
              boxShadow: 'none',
            }}
          >
            <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
              Emergency contact
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                }}
                src={'image.png'}
                alt={user?.emergencyContactInfo?.firstName}
              />
              <Box>
                <Typography variant="body1">
                  {user?.emergencyContactInfo?.firstName} {user?.emergencyContactInfo?.lastName || ''}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' } }} color="textSecondary">
                  {user?.emergencyContact || ''}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: '#f5f5f7',
              boxShadow: 'none',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1">Cane Phone number</Typography>
            <TextField
              disabled
              variant="outlined"
              fullWidth
              value={activeDevice?.deviceInfo?.phoneno || ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => handleCopyPhone(activeDevice)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ContentCopyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: '#f5f5f7',
              boxShadow: 'none',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
              Daily activity goal
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
              The daily activity norm is {goal} minutes walk per day. You can increase or decrease this indicator
              depending on your well-being and physical condition.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: '50px',
                p: '2px 8px',
              }}
            >
              <Typography variant="body1">{goal} min</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: '#f5f5f7',
              boxShadow: 'none',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1">CAN Emergency</Typography>
            {!isEmpty(emergencySubscription) ? (
              <>
                <Typography variant="body2" color="textSecondary">
                  Subscription will be renewed on {moment.unix(emergencySubscription?.lastPayment).format('YYYY/MM/DD')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch defaultChecked />
                  <Typography variant="body1">Enable for device</Typography>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  You are not subscribe for the emergency service.
                </Typography>
                <Button text="Subscribe" onClick={() => history('/can-emergency')} />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      <DeviceConfigurationsModal
        device={activeDevice}
        show={showDeviceConfigModal}
        onClose={() => setShowDeviceConfigModal(false)}
        user={user}
        cr={cr}
      />
    </Box>
  );
};

export default DeviceCard;
